// ─────────────────────────────────────────────────────────────────────────────
// pendingGenerations.js
//
// Tracks in-flight image/video generations across page reloads. The original
// HTTP request dies when the user refreshes, but the backend Generation
// record lives on in MongoDB and the upstream provider continues running --
// so the work isn't lost, only the client's connection to it is.
//
// Each pending entry carries a stable `requestId` (crypto.randomUUID) that
// is generated at dispatch time and stays with the entry through its entire
// lifecycle. Results are matched against `requestId`, never prompt text --
// this prevents a failed entry from claiming a future generation that
// happens to share the same prompt.
//
// A TTL reaper runs on every read: any pending entry older than 10 minutes
// with no match flips to status='failed' and is cleared from the map so it
// can never claim a future result.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'helix_pending_generations_v1';
const EXPIRY_MS   = 10 * 60 * 1000; // 10 minutes

const read = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const write = (map) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        /* quota or disabled -- best effort */
    }
};

/** Generate a stable request ID at dispatch time. */
export function createRequestId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'req-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

/**
 * Mark a clip as having an in-flight generation.
 *
 * @param {string} clipId
 * @param {object} info
 * @param {string} info.requestId  Stable UUID generated at dispatch time
 * @param {'image'|'video'} info.kind
 * @param {string} info.modelSlug
 * @param {string} info.prompt   (the resolved prompt sent to the provider)
 * @param {string} [info.aspectRatio]
 */
export function markPending(clipId, info) {
    if (!clipId) return;
    const map = read();
    map[clipId] = {
        ...info,
        status: 'pending',
        startedAt: Date.now(),
    };
    write(map);
}

/** Drop a clip's pending marker. Called on completion / failure / cancel. */
export function clearPending(clipId) {
    if (!clipId) return;
    const map = read();
    if (map[clipId]) {
        delete map[clipId];
        write(map);
    }
}

/**
 * Get all currently-pending generations, running the TTL reaper first.
 * Any entry older than EXPIRY_MS is removed entirely -- it can never
 * claim a future result. Failed entries are also purged.
 */
export function listPending() {
    const map = read();
    const now = Date.now();
    let mutated = false;
    for (const [clipId, info] of Object.entries(map)) {
        if (!info?.startedAt || now - info.startedAt > EXPIRY_MS) {
            console.warn(`[pending-reaper] clearing expired entry for clip ${clipId} (age: ${info?.startedAt ? Math.round((now - info.startedAt) / 1000) : '?'}s)`);
            delete map[clipId];
            mutated = true;
            continue;
        }
        if (info.status === 'failed') {
            delete map[clipId];
            mutated = true;
        }
    }
    if (mutated) write(map);
    return map;
}

/** Has a clip's generation aged past the expiry threshold? */
export function isExpired(info) {
    return !info?.startedAt || Date.now() - info.startedAt > EXPIRY_MS;
}
